/**
 * Stage-by-stage acquisition funnel rendered as a simple stepped bar chart.
 *
 * Design choice: visx's Sankey requires a proper multi-node graph (channels →
 * stages → tiers, etc). What the backend gives us is a flat list of stages
 * with counts — that's a linear funnel. A visx-Sankey would just degenerate
 * into a single horizontal line with decreasing widths, which is exactly what
 * a classic stepped funnel bar already shows, more legibly.
 *
 * When per-channel breakout lands and we want to show Channel → Stage fanning,
 * this component can be upgraded to a true Sankey. Marking the expansion
 * point clearly so future-us knows where to plug in.
 */
import React, { useMemo } from 'react';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { CEDAR, CATEGORICAL } from '../theme';
import { formatInt, formatPercent } from '../format';

export interface FunnelStage {
  stage: string;
  count: number;
  // null when there's no signup or no prior stage to compare against (renders N/A).
  pct_of_signup: number | null;
  pct_of_prior: number | null;
}

export interface AcquisitionFunnelProps {
  stages: FunnelStage[];
  height?: number;
}

const STAGE_LABELS: Record<string, string> = {
  waitlist: 'Waitlist',
  signup: 'Signup',
  onboarding_complete: 'Onboarding complete',
  first_goal: 'First goal',
  first_workout: 'First workout',
  trial_to_paid: 'Trial → Paid',
  upgrade_to_chat: 'Upgrade to Chat+',
  upgrade_to_pro: 'Upgrade to Pro',
  annual: 'Annual conversion',
  // Goal-lifecycle stages (ProductTab reuses this component) — previously
  // fell through to raw snake_case stage strings.
  discovery: 'Discovery',
  refinement: 'Refinement',
  awaiting_confirm: 'Awaiting confirmation',
  confirmed: 'Goal confirmed',
  plan_generated: 'Plan generated',
  milestone_reached: 'Milestone reached',
};

const MARGIN = { top: 12, right: 24, bottom: 12, left: 200 };

export const AcquisitionFunnel: React.FC<AcquisitionFunnelProps> = ({ stages, height }) => {
  // Filter out stages with 0 count if they're trailing (keep middle zeros so the
  // shape communicates drop-off honestly).
  const ordered = useMemo(() => {
    // Always anchor to the signup stage as the base for the bar widths
    const signup = stages.find((s) => s.stage === 'signup');
    const base = signup?.count ?? Math.max(1, ...stages.map((s) => s.count));
    return stages
      .filter((s) => s.stage !== 'waitlist' || s.count > 0) // hide waitlist if unused
      .map((s) => ({ ...s, _baseCount: base }));
  }, [stages]);

  const rowH = 42;
  const autoHeight = ordered.length * rowH + MARGIN.top + MARGIN.bottom;
  const finalHeight = height ?? autoHeight;

  if (!ordered.length) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-500">
        No funnel activity in this range yet.
      </div>
    );
  }

  return (
    <div style={{ height: finalHeight }} className="w-full">
      <ParentSize>
        {({ width }) => (
          <FunnelInner
            width={width}
            height={finalHeight}
            stages={ordered}
            base={ordered[0]._baseCount}
          />
        )}
      </ParentSize>
    </div>
  );
};

const FunnelInner: React.FC<{
  width: number;
  height: number;
  stages: (FunnelStage & { _baseCount: number })[];
  base: number;
}> = ({ width, height, stages, base }) => {
  const innerW = Math.max(120, width - MARGIN.left - MARGIN.right);
  const rowH = 42;

  return (
    <svg width={width} height={height}>
      <Group left={MARGIN.left} top={MARGIN.top}>
        {stages.map((s, i) => {
          const frac = base > 0 ? s.count / base : 0;
          const barW = Math.max(2, innerW * frac);
          const y = i * rowH;
          const color = i === 0 ? CEDAR.prime : CATEGORICAL[(i - 1) % CATEGORICAL.length];
          const label = STAGE_LABELS[s.stage] ?? s.stage;
          return (
            <g key={s.stage}>
              {/* Label */}
              <text
                x={-12}
                y={y + rowH / 2 - 2}
                textAnchor="end"
                fontSize={12}
                fontWeight={600}
                fontFamily="Inter, system-ui, sans-serif"
                fill="var(--text-strong)"
                dominantBaseline="middle"
              >
                {label}
              </text>
              {/* Background rail */}
              <rect
                x={0}
                y={y + 6}
                width={innerW}
                height={rowH - 16}
                rx={4}
                fill="var(--surface-muted)"
              />
              {/* Bar */}
              <rect
                x={0}
                y={y + 6}
                width={barW}
                height={rowH - 16}
                rx={4}
                fill={color}
                opacity={0.92}
              />
              {/* Count inside/after bar */}
              <text
                x={barW + 8 < innerW - 60 ? barW + 8 : innerW + 8}
                y={y + rowH / 2 - 2}
                fontSize={11}
                fontWeight={600}
                fontFamily="Inter, system-ui, sans-serif"
                fill={barW + 8 < innerW - 60 ? 'var(--text-primary)' : 'var(--text-strong)'}
                dominantBaseline="middle"
              >
                {formatInt(s.count)}
              </text>
              {/* Conversion chip on the far right */}
              <text
                x={innerW + 8}
                y={y + rowH / 2 + 10}
                fontSize={10}
                fontFamily="Inter, system-ui, sans-serif"
                fill="var(--text-tertiary)"
                dominantBaseline="middle"
              >
                {i === 0 ? 'base' : formatPercent(s.pct_of_signup)}
              </text>
            </g>
          );
        })}
      </Group>
    </svg>
  );
};
